/**
 * Cache Manager Service
 * Provides utilities for working with the Cache API and service worker
 */

// Check if we're in PWA mode
export const isPwa = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator.standalone === true) || 
         document.referrer.includes('android-app://');
};

// Check if the service worker is active and ready
export const isServiceWorkerReady = async () => {
  if (!('serviceWorker' in navigator)) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return !!registration.active;
  } catch (error) {
    console.error('Error checking service worker status:', error);
    return false;
  }
};

// List all available caches
export const listCaches = async () => {
  if (!('caches' in window)) return [];
  
  try {
    return await caches.keys();
  } catch (error) {
    console.error('Error listing caches:', error);
    return [];
  }
};

// Clear all API caches (useful for logout)
export const clearApiCaches = async () => {
  if (!('caches' in window)) return;
  
  try {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter(name => name.includes('api-'))
        .map(name => caches.delete(name))
    );
    console.log('API caches cleared');
  } catch (error) {
    console.error('Error clearing API caches:', error);
  }
};

// Prefetch a specific route for offline use
export const prefetchRoute = async (route) => {
  if (!navigator.serviceWorker.controller) return;
  
  try {
    // Send message to service worker to prefetch the route
    navigator.serviceWorker.controller.postMessage({
      type: 'PREFETCH_CACHE',
      payload: {
        routes: [route],
        apis: []
      }
    });
    console.log(`Route prefetched: ${route}`);
  } catch (error) {
    console.error('Error prefetching route:', error);
  }
};

// Prefetch API data for offline use
export const prefetchApiData = async (endpoint, authToken = null) => {
  if (!navigator.onLine) return;
  
  try {
    // Prepare headers
    const headers = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Use fetch API to prefetch data (will be cached by service worker)
    await fetch(endpoint, {
      headers,
      method: 'GET',
      credentials: 'same-origin',
    });
    
    console.log(`API data prefetched: ${endpoint}`);
  } catch (error) {
    console.error(`Error prefetching API data (${endpoint}):`, error);
  }
};

// Check if a specific route is cached
export const isRouteCached = async (route) => {
  if (!('caches' in window)) return false;
  
  try {
    // Check if the route exists in any cache
    const cache = await caches.match(new Request(route));
    return !!cache;
  } catch (error) {
    console.error('Error checking cached route:', error);
    return false;
  }
};

// Check cache status for mobile routes
export const checkMobileRouteCache = async () => {
  if (!('caches' in window)) return {};
  
  const mobileRoutes = [
    '/m-dashboard',
    '/m-account',
    '/m-journey/view/',
    '/m-treasure-chest',
    '/m-notifications'
  ];
  
  const results = {};
  
  for (const route of mobileRoutes) {
    results[route] = await isRouteCached(route);
  }
  
  return results;
};

// Warm up the cache on app start
export const warmupCache = async (authToken = null) => {
  if (!isPwa() || !navigator.onLine) return;
  
  // Essential API endpoints for mobile
  const essentialApis = [
    '/v2/journey/list-templates',
    '/v2/journey/list-reusable-templates',
    '/v2/user/get-user-info'
  ];
  
  // Prefetch each API endpoint
  for (const endpoint of essentialApis) {
    await prefetchApiData(endpoint, authToken);
  }
  
  console.log('Cache warmup completed');
};

// Register event listeners for cache events
export const registerCacheListeners = (store) => {
  if (!store) {
    console.warn('Store not provided to registerCacheListeners');
    return;
  }
  
  // Listen for offline/online events
  window.addEventListener('offline', () => {
    console.log('App went offline');
    store.commit('offline/SET_OFFLINE');
  });
  
  window.addEventListener('online', () => {
    console.log('App is back online');
    store.commit('offline/SET_ONLINE');
    
    // If in PWA mode, check for updates when back online
    if (isPwa() && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          registration.update();
        }
      });
    }
  });
  
  // Set initial state based on navigator.onLine
  if (!navigator.onLine) {
    store.commit('offline/SET_OFFLINE');
  }
}; 